import { OperationVariables, gql } from '@apollo/client';
import client from 'graphql/client';
import {
  applicationBasicFragment,
  commentsFragment,
  ratingsByEmployerFragment,
} from 'redux/application/queries';
import accessFragment from 'redux/job/queries';

interface Payload {
  variables: object;
  recache: Boolean;
}

const projectSmartSortProcessingStatusFragment = gql`
  fragment ProjectSmartSortProcessingStatus on Project {
    projectSmartSortProcessingStatus {
      processed
      pending
    }
  }
`;

export const fetchProjectSmartSortProcessingStatusGraphql = (projectId: number) => {
  const query = gql`
    query projectSmartSortProcessingStatus($id: Int!) {
      project(id: $id) {
        id
        ...ProjectSmartSortProcessingStatus
      }
    }
    ${projectSmartSortProcessingStatusFragment}
  `;

  return client.query({
    query,
    variables: {
      id: projectId,
    },
    fetchPolicy: 'network-only',
  });
};

const fetchProjectGraphql = (payload: Payload) => {
  const params: OperationVariables = {};
  const { variables } = payload;

  if (payload.recache) {
    params.fetchPolicy = 'network-only';
  }

  const query = gql`
    query fetchProject(
      $id: Int!
      $rating: [Rating]
      $selectionLevel: [SelectionLevel]
      $selectionLevelValue: [Int]
      $preselectionPassed: PreselectionPassed
      $rejected: Rejected
      $candidateName: String
      $sortField: String
      $sortDirection: String
      $tags: [Int]
      $skipTags: [Int]
      $email: String
      $filterByJobId: Int
      $fromSite: [Int]
      $preselectionFilter: [[Int!]!]
      $logSearch: Boolean
    ) {
      project(id: $id) {
        id
        name
        timeCreated
        applicationCount
        customSelectionLabels {
          id
          projectId
          selectionLevel
          name
          hidden
        }
        selectionLevels {
          id
          level
          name
          hidden
          order
          sentiment
        }
        hiredColumnRenamed
        accessType
        access {
          ...Access
        }
        projectSmartSort {
          enabled
        }
        ...ProjectSmartSortProcessingStatus
        applications(
          rating: $rating
          selectionLevel: $selectionLevel
          selectionLevelValue: $selectionLevelValue
          preselectionPassed: $preselectionPassed
          rejected: $rejected
          candidateName: $candidateName
          sortField: $sortField
          sortDirection: $sortDirection
          skipTags: $skipTags
          tags: $tags
          email: $email
          filterByJobId: $filterByJobId
          fromSite: $fromSite
          preselectionFilter: $preselectionFilter
          logSearch: $logSearch
        ) {
          id
          candidateProfileId
          applicationType
          anonymizedApplicationState
          jobId
          job {
            id
            title
          }
          preselectionJobId
          lockedCandidate {
            cityName
            standardizedPositionName
            workExperienceCompanyName
            workExperienceFromDate
            workExperienceToDate
            candidateProfileId
          }
          ...BasicFields
          selectionLevelValue
          ...RatingsByEmployerFields
          order
          applicationTime
          fromSite
          comments {
            ...CommentsFields
          }
          events(detailed: false) {
            id
            eventType
          }
          relevance
          processingStatus
          summary
          feedback
          feedbackComment
          tags {
            id
            name
            special_type
          }
          calendarSlots {
            id
            title
            status
            cancelled
            datetimeFrom
            datetimeTo
            calendarEmployers {
              id
              organizer
              employer {
                id
                name
              }
            }
            calendarCandidates {
              application {
                id
              }
              status
            }
          }
        }
        jobs {
          id
          title
          status
          online
          application_count
          url
          applicationDeliveryMethod
          receiptId
          accessType
          paymentId
          showHwModal
          standardizedPositionId
          primaryCategoryId
          siteVisibility
          datePosted
          expireDate
          dateTimeCreated
          jobType
          jobSmartSort {
            enabled
          }
          cities {
            id
          }
          regions {
            id
          }
          preselection {
            id
            preselectionMaxScore
            preselectionQuestion {
              id
              question
              commentAllowed
              genericQuestion
              genericQuestionType
              preselectionQuestionAnswers {
                id
                answer
              }
            }
          }
          virtualDayOfCareerAndKnowledgeFairAd
          renewedFrom
          didYouHireFeedback
          didYouHireFeedbackUrgency
          hiringOutcome {
            action
            answeredAt
            reason
            reasonText
          }
        }
      }
    }
    ${applicationBasicFragment}
    ${accessFragment}
    ${commentsFragment}
    ${ratingsByEmployerFragment}
    ${projectSmartSortProcessingStatusFragment}
  `;

  return client.query({
    query,
    variables,
    ...params,
  });
};

const mutateCustomSelectionLabelsGraphql = (payload: Payload) => {
  const params: OperationVariables = {};
  const { variables } = payload;

  if (payload.recache) {
    params.fetchPolicy = 'network-only';
  }

  const mutation = gql`
    mutation projectCustomSelectionLabels(
      $customSelectionLabel: [ProjectSelectionLabelInput]
      $projectId: Int
    ) {
      projectCustomSelectionLabels(
        customSelectionLabel: $customSelectionLabel
        projectId: $projectId
      ) {
        id
        hiredColumnRenamed
        customSelectionLabels {
          id
          projectId
          selectionLevel
          name
          hidden
        }
        selectionLevels {
          id
          level
          name
          hidden
          order
          sentiment
        }
      }
    }
  `;

  return client.mutate({
    mutation,
    variables,
    ...params,
  });
};

const fetchApplicationFiltersGql = (projectId: number) => {
  const query = gql(`
     query applicationFilters($projectId: Int!) {
      applicationFilters(projectId: $projectId) {
          preselection {
              name
              count
          }
          selectionLevel {
              name
              count
          }
          selectionLevels {
              name
              value
              count
          }
          rating {
              applicationRatingGrades {
                  name
                  count
              }
              employersRating {
                  count
                  employer {
                      id
                      name
                  }
              }
          }
          rejection {
              name
              count
          }
          tags {
              name
              count
              results {
                  tag
                  tag_type
                  id_tag
                  applications_count
              }
          }
          emails {
              name
              count
          }
          preselectionJobs {
              jobId
              title
              datePosted
              questions {
                  questionId
                  question
                  answers {
                      answerId
                      answer
                  }
              }
          }
      }
  }
  `);

  return client.query({
    query,
    variables: { projectId },
    fetchPolicy: 'network-only',
  });
};

export const fetchSelectionLevelConfigGraphql = (payload: Pick<Payload, 'variables'>) => {
  const { variables } = payload;

  const query = gql`
    query selectionLevelConfig($id: Int!) {
      project(id: $id) {
        id
        selectionLevels {
          id
          level
          name
          hidden
          order
          sentiment
        }
        customSelectionLabels {
          id
          projectId
          selectionLevel
          name
          hidden
        }
        hiredColumnRenamed
      }
    }
  `;

  return client.query({
    query,
    variables,
    fetchPolicy: 'network-only',
  });
};

export default fetchProjectGraphql;
export { fetchApplicationFiltersGql, mutateCustomSelectionLabelsGraphql };

const fetchProjectPPPGraphql = (projectId: number) => {
  const query = gql`
    query fetchProjectPPP($id: Int!) {
      project(id: $id) {
        id
        projectHasJobWithPPP
      }
    }
  `;

  return client.query({
    query,
    variables: { id: projectId },
    fetchPolicy: 'network-only',
  });
};

const fetchProjectPPPLockedApplicationsGraphql = (projectId: number) => {
  const query = gql`
    query fetchProjectPPPLockedApplications($id: Int!) {
      project(id: $id) {
        id
        pppLockedApplications
      }
    }
  `;

  return client.query({
    query,
    variables: { id: projectId },
    fetchPolicy: 'network-only',
  });
};

export { fetchProjectPPPGraphql, fetchProjectPPPLockedApplicationsGraphql };
